import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { IconButton, MenuItem, Select } from "@mui/material";
import React, { useMemo } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { useTranslation } from "react-i18next";
const ROW_PER_PAGES = [{ value: 10 }, { value: 20 }, { value: 50 }, { value: 100 }];

const buildPageItems = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set([1, totalPages, currentPage]);
  if (currentPage > 1) pages.add(currentPage - 1);
  if (currentPage < totalPages) pages.add(currentPage + 1);

  const sorted = [...pages].sort((a, b) => a - b);
  const items = [];

  sorted.forEach((p, index) => {
    if (index > 0 && p - sorted[index - 1] > 1) {
      items.push("ellipsis");
    }
    items.push(p);
  });

  return items;
};

const boxBtnSx = {
  minWidth: 40,
  width: 40,
  height: 40,
  borderRadius: "8px",
  border: "1px solid #E2E8F0",
  backgroundColor: "#fff",
  color: "#374151",
  p: 0,
  "&:hover": { backgroundColor: "#F9FAFB" },
  "&.Mui-disabled": {
    opacity: 0.45,
    borderColor: "#E5E7EB",
  },
};

const pageBtnSx = {
  ...boxBtnSx,
  fontSize: 14,
  fontWeight: 500,
};

const selectSx = {
  minWidth: 72,
  height: 36,
  borderRadius: "8px",
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D1D5DB",
  },
  "& .MuiSelect-select": {
    py: "6px",
    pl: "12px !important",
    pr: "32px !important",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    right: "8px",
    color: "#374151",
  },
};

const CustomPagination = ({
  page = 0,
  count = 0,
  rowsPerPage = ROW_PER_PAGES[0].value,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
}) => {
  const { t } = useTranslation();
  // Nếu pageSize ngoài danh sách (vd. bị set = số dòng), fallback về option gần nhất
  const allowedSizes = ROW_PER_PAGES.map((item) => item.value);
  const safeRowsPerPage = allowedSizes.includes(rowsPerPage)
    ? rowsPerPage
    : allowedSizes.includes(50)
      ? 50
      : allowedSizes[0];
  const totalPages = Math.max(Math.ceil(count / safeRowsPerPage), 1);
  const currentPage = page + 1;

  const pageItems = useMemo(
    () => buildPageItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const rangeStart = count > 0 ? safeRowsPerPage * currentPage - safeRowsPerPage + 1 : 0;
  const rangeEnd = Math.min(currentPage * safeRowsPerPage, count);

  return (
    <div className="flex justify-between items-center gap-4 flex-wrap w-full">
      <div className="flex items-center gap-3">
        <Select
          value={safeRowsPerPage}
          onChange={(e) => onRowsPerPageChange(e.target.value)}
          sx={selectSx}
          IconComponent={(props) => (
            <IoIosArrowDown {...props} size={16} />
          )}
        >          {ROW_PER_PAGES.map(({ value }) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>

        {count > 0 && (
          <span className="text-sm text-[#374151] whitespace-nowrap">
            {rangeStart}-{rangeEnd} {t("common.of")} {count}{" "}
            {t(count === 1 ? "common.item" : "common.items")}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <IconButton
          sx={boxBtnSx}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 0}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        {pageItems.map((item, index) =>
          item === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="px-1 text-sm text-[#6B7280] select-none"
            >
              ...
            </span>
          ) : (
            <IconButton
              key={item}
              sx={{
                ...pageBtnSx,
                ...(item === currentPage
                  ? {
                      borderColor: "#0057FF",
                      color: "#ffffff",
                      backgroundColor: "#0057FF",
                      "&:hover": {
                        backgroundColor: "#0046CC",
                        borderColor: "#0046CC",
                      },
                    }
                  : {
                      "&:hover": {
                        backgroundColor: "#F5F8FF",
                        borderColor: "#0057FF",
                        color: "#0057FF",
                      },
                    }),
              }}
              onClick={() => onPageChange(item - 1)}
            >
              {item}
            </IconButton>
          )
        )}

        <IconButton
          sx={boxBtnSx}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
};

export default CustomPagination;
